import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    ChevronRight,
    ArrowRight,
    CheckCircle2,
    AlertCircle,
    Search,
    ShieldCheck,
    History,
    MessageSquareWarning,
    FileSearch,
    ClipboardList,
    ThumbsUp,
    Lock
} from 'lucide-react';
import './IncidentManagement.css';

const IncidentManagement = () => {
    useEffect(() => {
        document.title = "Incident Management & DuPont Workflow | X-Max";
        let metaDesc = document.querySelector('meta[name="description"]');
        if (metaDesc) {
            metaDesc.setAttribute('content', 'Systematically report and investigate safety incidents using X-Max Incident Management. 9-step DuPont workflow for root cause analysis and corrective action.');
        }
        window.scrollTo(0, 0);
    }, []);

    const timelineSteps = [
        {
            num: 1,
            title: "Incident / Near Miss Occurrence",
            desc: "Immediate first aid, secure the scene, protect evidence. Notify supervisor within 1 hour.",
            icon: <AlertCircle size={20} />
        },
        {
            num: 2,
            title: "Initial Notification & Classification",
            desc: "Classify incident type: Near Miss, First Aid, Medical Treatment, Lost Time, Fatality. Trigger escalation matrix based on severity.",
            icon: <MessageSquareWarning size={20} />
        },
        {
            num: 3,
            title: "Incident Reporting",
            desc: "Log in X-Max within 24 hours. Capture: date/time, location, people involved, equipment, photos, and initial description.",
            icon: <ClipboardList size={20} />
        },
        {
            num: 4,
            title: "Preliminary Investigation",
            desc: "Supervisor-led investigation within 24–48 hours. Identify immediate causes and contributing factors using DuPont's STOP methodology.",
            icon: <FileSearch size={20} />
        },
        {
            num: 5,
            title: "Root Cause Analysis (RCA)",
            desc: "Apply 5-Why or Fault Tree Analysis. Identify systemic, human, and equipment root causes. Use X-Max's Why-Why Analysis module.",
            icon: <Search size={20} />
        },
        {
            num: 6,
            title: "CAPA Development",
            desc: "Define Corrective & Preventive Actions with owners, deadlines, and priority levels. Link to X-Max CAPA module for tracking.",
            icon: <ShieldCheck size={20} />
        },
        {
            num: 7,
            title: "Review & Approval",
            desc: "Safety Manager and Plant Head review findings and approve CAPA plan. Escalate to leadership for LTI or fatality cases.",
            icon: <ThumbsUp size={20} />
        },
        {
            num: 8,
            title: "CAPA Implementation & Verification",
            desc: "Execute corrective actions. Verify effectiveness through follow-up audits and observations. Close only when verified.",
            icon: <CheckCircle2 size={20} />
        },
        {
            num: 9,
            title: "Incident Closure & Learnings",
            desc: "Formal closure sign-off. Share One Point Lesson (OPL) across all plants. Update risk register and training matrix.",
            icon: <Lock size={20} />
        }
    ];

    return (
        <div className="incident-page">
            {/* Hero Section */}
            <section className="incident-hero">
                <div className="container incident-hero-container">
                    <div className="incident-hero-content">
                        <div className="sec-tag">CORE MODULES</div>
                        <h1>Incident <span className="gradient-text">Management</span></h1>
                        <p className="hero-desc">
                            X-Max's Incident Management module provides a robust framework for reporting and investigating near-misses and actual incidents.
                        </p>
                        <ul className="hero-bullet-list">
                            <li><ArrowRight size={20} className="bullet-icon" /> Report Incident (including near-misses)</li>
                            <li><ArrowRight size={20} className="bullet-icon" /> Investigate Root Cause (using tools like 5-Why)</li>
                            <li><ArrowRight size={20} className="bullet-icon" /> Implement Corrective Actions (CAPA)</li>
                            <li><ArrowRight size={20} className="bullet-icon" /> Learn & Prevent Recurrence</li>
                        </ul>
                        <p className="hero-subtext">
                            Ensuring a systematic approach to understanding root causes, preventing recurrence, and fostering a proactive safety culture where every incident becomes a learning opportunity.
                        </p>
                        <Link to="/book-now" className="btn btn-primary btn-lg">
                            Book Demo <ChevronRight size={20} />
                        </Link>
                    </div>
                    <div className="incident-hero-visual">
                        <div className="visual-wrapper">
                            <img
                                src="./assets/modules/safety.png"
                                alt="Incident Reporting Interface"
                                className="hero-img"
                            />
                            <div className="visual-overlay"></div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Timeline Section */}
            <section className="incident-workflow">
                <div className="container">
                    <div className="section-header">
                        <div className="sec-tag">LIFECYCLE</div>
                        <h2>Incident Reporting to Closure: <span className="gradient-text">DuPont Workflow</span></h2>
                        <p>X-Max follows DuPont's world-class incident management framework — ensuring every incident is systematically reported, investigated, and closed with full accountability.</p>
                    </div>

                    <div className="timeline-container">
                        <div className="timeline-line"></div>
                        {timelineSteps.map((step, index) => (
                            <div key={index} className={`timeline-item ${index % 2 === 0 ? 'left' : 'right'}`}>
                                <div className="timeline-marker">
                                    <span className="step-number">{step.num}</span>
                                </div>
                                <div className="timeline-content">
                                    <div className="content-header">
                                        <div className="content-icon">{step.icon}</div>
                                        <h3>{step.title}</h3>
                                    </div>
                                    <p>{step.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Detail Content Section */}
            <section className="incident-details">
                <div className="container">
                    <div className="details-card">
                        <div className="details-text">
                            <p>Our platform simplifies the entire incident lifecycle. Employees can easily report near-misses and incidents via a mobile app, including photos and precise details, triggering instant escalation.</p>
                            <p>The system then guides thorough investigations, integrating tools like the <strong>5-Why analysis</strong> to uncover root causes. Finally, it links directly to our <strong>CAPA System</strong> to ensure corrective and preventive actions are assigned, tracked, and verified, closing the loop on safety improvements.</p>
                        </div>
                        <div className="details-visual">
                            <History size={120} className="rotating-icon" />
                        </div>
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="incident-footer-cta">
                <div className="container">
                    <div className="cta-card">
                        <h2>Every incident is a lesson. Automate yours now.</h2>
                        <Link to="/book-now" className="btn btn-primary btn-lg">Start Incident Audit</Link>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default IncidentManagement;
